import "./App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import DashboardLayout from "./components/DashboardLayout";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import TwoFactorAuth from "./pages/Auth/TwoFactorAuth";
import NewPassword from "./pages/Auth/ResetPassword";
import ResetPassword from "./pages/Auth/ResetPassworddl";
import Dashboard from "./pages/dashboard/Dashboard";
import Project from "./pages/projects/Project";
import TimeSheet from "./pages/timesheet/TimeSheet";
import OTSheetModule from "./pages/otsheet/OTSheetModule";
import Issues from "./pages/issues/Issues";
import Task from "./pages/tasks/Task";
import Notification from "./pages/Budget/Budget";
import Milestone from "./pages/milestones/Milestone";
import Calender from "./pages/calender/Calender";
import UtilisationPopupPreview from "./pages/utilisation/UtilisationPopupPreview";
import ProjectDetail from "./pages/projects/ProjectDetail";
import SubTaskList from "./components/ProjectModule/task/Subtasklist";
import TaskDetail from "./components/ProjectModule/task/TaskDetail";
import IssueDetail from "./components/ProjectModule/issues/IssueDetail";
import MilestoneDetail from "./components/ProjectModule/milestones/MilestoneDetails";
import TimesheetDetail from "./components/ProjectModule/timesheet/TimesheetDetail";
import MilestoneSubTasklist from "./components/ProjectModule/milestones/MilestoneSubTasklist";
import MilestoneTasklistDetailComponent from "./components/ProjectModule/milestones/MilestoneTasklistDetail";
import MilestoneIssueDetail from "./components/ProjectModule/milestones/issues/MilestoneIssueDetails";
import { useEffect, useMemo, useState } from "react";
import Settings from "./pages/settings/Settings";
import PortalUsersDetail from "./pages/settings/PortalUsersDetail";
import CreateUsers from "./pages/Auth/CreateUsers";
import Pagenotfound from "./components/common/Pagenotfound";
import Budget from "./pages/Budget/Budget";
// import TimesheetPopup from "./components/common/TimesheetPopup";
import ChangePassword from "./pages/Auth/ChangePassword";
import { showPasswordReminderPopup } from "./components/common/SweetPopup";
import { useFetchPasswordRecommentQuery } from "./slice/logdetailSlice";
import { useDispatch, useSelector } from "react-redux";
import { setloginUserName } from "./selectSlice";
import { useLoadCurrentUser } from "./hooks/useLoadCurrentUser";
import ProfileLoader from "./components/common/ProfileLoader";
import TaskAllocation from "./pages/taskallocation/TaskAllocation";
import ExportCommonTimesheet from "./pages/ExportTiesheet/ExportCommonTimesheet";
import OTReports from "./pages/OT Reports/OTReports";
import WeeklyTaskAllocation from "./pages/weeklytaskallocation/WeeklyTaskAllocation";
import TwoFactorScan from "./pages/Auth/TwoFactorScan";
import ManageLeave from "./pages/crm/Manageleave";
import Holiday from "./pages/Holiday/Holiday";
import AppraisalFeedback from "./pages/AppraisalFeedback/AppraisalFeedback";
import WorkforceStabilityReport from "./pages/WorkforceStability/WorkforceStability";
import Testimonial from "./pages/Testimonial/Testimonial";
import Certificate from "./pages/Certificate/Certificate"
import VerifyCertificate from "./pages/Certificate/VerifyCertificate";
import ResourceEarning from "./pages/ResourceEarning/ResourceEarning";



// Client-side sanity check only (the server remains the source of truth):
// rejects a missing token or a JWT whose `exp` claim has already passed.
// Tokens that are not decodable JWTs are left to the server to validate.
const hasValidToken = () => {
  const token = localStorage.getItem("access_token");
  if (!token) return false;
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (typeof payload.exp === "number" && payload.exp * 1000 <= Date.now()) {
      localStorage.removeItem("access_token");
      return false;
    }
  } catch {
    // not a JWT - fall through
  }
  return true;
};

// Pages that work without a logged-in profile (login, 2FA, password flows, public
// pages). The current user is only loaded from the API outside of these.
const PROFILE_FREE_PATHS = [
  "/login",
  "/forgot_password",
  "/twofactor-auth",
  "/twofactor-scan",
  "/testimonial",
  "/change-password",
];
const PROFILE_FREE_PREFIXES = [
  "/reset-password/",
  "/create-password/",
  "/certificate/verify/",
];
const isProfileFreePath = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return (
    PROFILE_FREE_PATHS.includes(path) ||
    PROFILE_FREE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
};

const ProtectedRoute = ({ element }) => {
  return hasValidToken() ? element : <Navigate to="/login" replace />;
};
const PublicRoute = ({ element }) => {
  const isAuthenticated = localStorage.getItem("access_token");
  const searchParams = new URLSearchParams(window.location.search);
  const ssoTarget = searchParams.get("sso_target");
  return (isAuthenticated && !ssoTarget) ? <Navigate to="/dashboard"/> : element;
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Initialize state directly from localStorage
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return Boolean(localStorage.getItem("access_token"));
  });
  const login_user = useSelector((state) => state.selectOptions?.loginUserName);
  const { role: login_user_role } = login_user || {};
  const effectiveRole = (login_user_role || localStorage.getItem("login_user_role"))?.toUpperCase();
  const [twoFactorToken, setTwoFactorToken] = useState(null); 

  // The logged-in user (id, name, email, role) comes from the API, not localStorage.
  const needsProfile =
    hasValidToken() && !login_user && !isProfileFreePath(location.pathname);
  const endSession = () => {
    localStorage.clear();
    sessionStorage.clear();
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
  };
  const { failed: profileFailed, retry: retryProfile } = useLoadCurrentUser({
    enabled: needsProfile,
    onUnauthorized: endSession,
  });

  useEffect(() => {
    const hasToken = Boolean(localStorage.getItem("access_token"));
    if (hasToken !== isAuthenticated) {
      setIsAuthenticated(hasToken);
    }
  }, [location, isAuthenticated]);

  // Multi-Tab & Cross-App Session Synchronization
  // 1. storage event: instantaneous same-origin tab sync (zero network overhead)
  // 2. visibilitychange: checks cross-app session revocation (e.g. Deals logout) when returning to this tab
  // NOTE: window.focus is intentionally omitted so button clicks and DevTools do not trigger checks.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "access_token" && !e.newValue) {
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    let lastCheckTime = 0;
    const handleVisibilityChange = () => {
      if (document.hidden || document.visibilityState !== "visible") return;
      if (!localStorage.getItem("access_token")) return;

      const now = Date.now();
      if (now - lastCheckTime < 5000) return; // 5-second debounce
      lastCheckTime = now;

      const token = localStorage.getItem("access_token");
      fetch(`${process.env.REACT_APP_API_BASE_URL}sso/session-user`, {
        headers: {
          "Accept": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        credentials: "include"
      })
        .then((res) => {
          if (res.status === 401) {
            // Session was revoked in connected app (e.g. Deals CRM logout)
            localStorage.clear();
            sessionStorage.clear();
            setIsAuthenticated(false);
            navigate("/login");
          }
        })
        .catch(() => {});
    };

    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [navigate]);
  const { data } = useFetchPasswordRecommentQuery(undefined, { skip: !isAuthenticated });

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const message = data?.[0].message;
      const user_id = data?.[0].user_id;
      const is_read = data?.[0].is_read;
      const notification_id = data?.[0].notification_id;
      showPasswordReminderPopup(
        message,
        user_id,
        is_read,
        notification_id,
        navigate
      );
    }
  }, [data]);

  if (needsProfile) {
    return (
      <ProfileLoader
        failed={profileFailed}
        onRetry={retryProfile}
        onLogout={endSession}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute
              element={<Login setIsAuthenticated={setIsAuthenticated}  setTwoFactorToken={setTwoFactorToken} />}
            />
          }
        />
        <Route
          path="/forgot_password"
          element={<PublicRoute element={<ForgotPassword />} />}
        />
        <Route path="/twofactor-auth" element={<TwoFactorAuth token={twoFactorToken} setIsAuthenticated={setIsAuthenticated} />} />
        <Route path="/twofactor-scan" element={<TwoFactorScan token={twoFactorToken} setIsAuthenticated={setIsAuthenticated} isActive={false}/> } />
        <Route path="/testimonial" element={<Testimonial />} />

        <Route
          path="/reset-password/:token"
          element={<PublicRoute element={<NewPassword />} />}
        />
        <Route
          path="/create-password/:token"
          element={<PublicRoute element={<CreateUsers />} />}
        />

        <Route
          path="/change-password"
          element={<ProtectedRoute element={<ChangePassword />} />}
        />

        <Route
          path="/certificate/verify/:token"
          element={<VerifyCertificate />}
        />

        <Route
          path="/"
          element={
            <Navigate to={localStorage.getItem("access_token") ? "/dashboard" : "/login"} replace />
          }
        />
        <Route
          element={
            hasValidToken() ? <DashboardLayout /> : <Navigate to="/login" replace />
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          {/* projects nested routes */}
          <Route path="projects" element={<Project />} />
          {(effectiveRole === "ADMIN" || effectiveRole === "MANAGER") && (
            <Route path="earning-dashboard/*" element={<ResourceEarning />} />
          )}
          {/* {(login_user_role === "ADMIN" || login_user_role === "MANAGER") && ( */}
          <Route
            path="projects/projectDetail/:id/*"
            element={<ProjectDetail />}
          />

          <Route path="Timesheet" element={<TimeSheet />} />
          {(login_user_role === "ADMIN" || login_user_role === "MANAGER") && (
            <Route path="Project-Reports" element={<ExportCommonTimesheet />} />
          )}

          <Route path="Issues" element={<Issues />} />
          <Route
            path="projects/projectDetail/:project_id/issue/IssueDetail/:id"
            element={<IssueDetail />}
          />
          <Route path="Tasks" element={<Task />} />
          <Route path="Task-Allocation/*" element={<TaskAllocation />} />
          <Route path="Weekly-Task" element={<WeeklyTaskAllocation />} />

          <Route
            path="projects/projectDetail/:id/taskdetail"
            element={<TaskDetail />}
          />
          {(login_user_role === "ADMIN" || login_user_role === "MANAGER") && (
            <Route path="budget" element={<Budget />} />
          )}

          <Route path="settings/*" element={<Settings />} />
          <Route
            path="settings/portalUsersDetail/:id"
            element={<PortalUsersDetail />}
          />
          <Route path="/OT-Sheet/*" element={<OTSheetModule />} />
          <Route path="/OT-Report-Sheet" element={<OTReports />} />
          <Route path="Workforce-Stability" element={<WorkforceStabilityReport/>}/>

          <Route path="Milestones" element={<Milestone />} />
          <Route
            path="projects/projectDetail/milestones/MilestoneDetail/:id"
            element={<MilestoneDetail />}
          />
          <Route path="calender" element={<Calender />} />
          {/* Static design preview of the utilisation popup; not routed in production builds. */}
          {process.env.NODE_ENV !== "production" && (
            <Route path="utilisation-preview" element={<UtilisationPopupPreview />} />
          )}
          <Route path="projects" element={<Project />} />
          <Route
            path="projects/projectDetail/:id/*"
            element={<ProjectDetail />}
          />

          {/* <Route path="timesheet" element={<TimeSheet />} /> */}
          {/* <Route path="issues" element={<Issues />} /> */}
          <Route
            path="projects/projectDetail/issue/IssueDetail/:id"
            element={<IssueDetail />}
          />
          {/* <Route path="tasks" element={<Task />} /> */}
          <Route
            path="projects/projectDetail/:project_id/taskdetail/:id"
            element={<TaskDetail />}
          />
          <Route path="notifications" element={<Notification />} />
          {/* <Route path="milestones" element={<Milestone />} /> */}
          <Route
            path="projects/projectDetail/:project_id/milestone/MilestoneDetail/:id"
            element={<MilestoneDetail />}
          />
          <Route
            path="/projects/projectDetail/:project_id/milestone/MilestoneDetail/:milestone_id/MilestoneSubTasklist/:task_list_id"
            element={<MilestoneSubTasklist />}
          />
          <Route
            path="/projects/projectDetail/:project_id/milestone/MilestoneDetail/:milestone_id/MilestoneSubTasklist/:task_list_id/TasklistDetail/:id"
            element={<MilestoneTasklistDetailComponent />}
          />
          <Route
            path="/projects/projectDetail/:project_id/milestone/MilestoneDetail/:milestone_id/MilestoneIssueDetail/:id"
            element={<MilestoneIssueDetail />}
          />
          {/* <Route path='calender' element={<Calender />} /> */}
          {/* Add other routes as nested routes here */}
          <Route
            path="projects/projectDetail/:id/timeSheet/TimeSheetDetail/:id"
            element={<TimesheetDetail />}
          />
          <Route path="Manage-Leave/*" element={< ManageLeave/>} />
          <Route path="Holiday" element={< Holiday/>} />
          <Route path="Appraisal-Feedback" element={<AppraisalFeedback />} />
          <Route path="Certificates" element={<Certificate/>}/>
          <Route path="*" element={<Pagenotfound />} />
          <Route path="/pagenotfound" element={<Pagenotfound />} />
        </Route>

      </Routes>
      {/* <TimesheetPopup /> */}
    </>
  );
}

export default App;
